/**
 * Filter Persistence System — Page-Level Lifecycle Hook
 *
 * `usePagePersistence` complements `usePersistentState` with page-level
 * lifecycle utilities: clearing all persisted state for a page, wiring
 * up logout handlers, and (in future) broadcasting changes cross-tab.
 *
 * Usage:
 *   // At the top of any page component:
 *   const { clearPageState } = usePagePersistence({ storageKey: "my-inventory" });
 *
 *   // In a logout handler (anywhere in the app):
 *   import { clearAllPersistence } from "./persistence";
 *   clearAllPersistence();
 */

import { useCallback, useEffect } from "react";
import { clearNamespace, clearAllPersistence } from "./storageAdapter";
import { PersistenceConfig, PagePersistenceAPI } from "./types";

// Custom DOM event name fired when a namespace is cleared.
// Other components can listen for it to refresh their local state.
const CLEAR_EVENT = "wp:persistence:cleared";

export function usePagePersistence(config: PersistenceConfig): PagePersistenceAPI {
  const {
    storageKey,
    backend = "session",
    broadcastClear = false,
  } = config;

  // ── Clear all state for this page ─────────────────────────────────────────
  const clearPageState = useCallback(() => {
    clearNamespace(storageKey, backend);

    if (broadcastClear) {
      try {
        window.dispatchEvent(
          new CustomEvent(CLEAR_EVENT, { detail: { storageKey } })
        );
      } catch {
        // Ignore in environments without a full DOM (e.g. tests).
      }
    }
  }, [storageKey, backend, broadcastClear]);

  return { clearPageState };
}

/**
 * Call this in the app's logout handler to nuke all persisted filter state.
 * Re-exported here so callers don't need to import from storageAdapter directly.
 *
 * Example:
 *   import { clearAllPersistenceOnLogout } from "hooks/persistence";
 *   // in logout handler:
 *   clearAllPersistenceOnLogout();
 */
export { clearAllPersistence as clearAllPersistenceOnLogout };
